/**
 * Karma (ฝ่ายธรรมะ) from player killing: ดี good, ปกติ normal, ชั่ว evil, บ้า mad.
 * What a killer becomes depends on what they were and whom they killed.
 */
export type Karma = 'good' | 'normal' | 'evil' | 'mad';

export const KARMA_LABELS: Readonly<Record<Karma, string>> = {
  good: 'ดี',
  normal: 'ปกติ',
  evil: 'ชั่ว',
  mad: 'บ้า',
};

/** PK points earned for each player killed, and the thresholds of the head colours. */
export const PK = { perKill: 200, red: 2000, purple: 5000 } as const;

/** Name colour from PK points: plain, orange (has killed), red (over 2000), purple (over 5000). */
export type PkColor = 'none' | 'orange' | 'red' | 'purple';
export function pkColor(points: number): PkColor {
  return points > PK.purple ? 'purple' : points > PK.red ? 'red' : points > 0 ? 'orange' : 'none';
}

/** Jail time for a red name who dies: 10 minutes, rising to 30 with PK points (seconds). */
export function jailSeconds(points: number): number {
  const k = Math.min(1, Math.max(0, (points - PK.red) / (PK.purple * 2 - PK.red)));
  return Math.round((10 + 20 * k) * 60);
}

/**
 * The killer's karma after killing a player of the given karma:
 * - ดี ฆ่า ดี → บ้า · ดี ฆ่า ชั่ว → ดี · ดี ฆ่า ปกติ → ชั่ว
 * - ชั่ว ฆ่า ดี → ชั่ว · ชั่ว ฆ่า ชั่ว → บ้า · ชั่ว ฆ่า ปกติ → ชั่ว
 * - ปกติ ฆ่า ปกติ/ดี → ชั่ว · ปกติ ฆ่า ชั่ว/บ้า → ดี
 * - บ้า stays บ้า; killing a บ้า counts like killing a ชั่ว.
 */
export function karmaAfterKill(killer: Karma, victim: Karma): Karma {
  if (killer === 'mad') return 'mad';
  const bad = victim === 'evil' || victim === 'mad';
  if (killer === 'good') return victim === 'good' ? 'mad' : bad ? 'good' : 'evil';
  if (killer === 'evil') return bad ? 'mad' : 'evil';
  return bad ? 'good' : 'evil';
}

/**
 * Newbie protection (H26): below this power rank (cultivation.ts `rankFor` index; 9 = ฝึกลมปราณ ชั้น 1) a player
 * cannot turn PK on, and other players cannot hurt, lock, kidnap or ult-catch them in the open world (duels,
 * arena and ladder stay). The kidnap rule (data/offlineWork.ts) uses the same line.
 */
export const PK_MIN_RANK = 9;
/** True while a player of this power rank index is protected. */
export const isPkProtected = (rankIndex: number): boolean => rankIndex < PK_MIN_RANK;
